import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertCircle, CheckCircle2, Copy, Sparkles, RotateCcw, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { Workflow } from '@/types/workflow';
import { WorkflowDefinitionSchema, generateWorkflowSchemaPrompt } from '@ponder/artifacts/workflows';

// Generate the system prompt from the actual schema
const DEFAULT_WORKFLOW_SCHEMA = generateWorkflowSchemaPrompt();

interface AIGenerationTabProps {
  workflow: Workflow;
  onWorkflowUpdate: (workflow: Workflow) => void;
}

interface LLMProvider {
  name: string;
  configured: boolean;
  models?: string[];
}

export function AIGenerationTab({ workflow, onWorkflowUpdate }: AIGenerationTabProps) {
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [provider, setProvider] = useState<'openrouter' | 'azure' | 'google'>('openrouter');
  const [model, setModel] = useState('');
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_WORKFLOW_SCHEMA);
  const [userPrompt, setUserPrompt] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(4000);
  const [generatedJSON, setGeneratedJSON] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isValidJSON, setIsValidJSON] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [isErrorExpanded, setIsErrorExpanded] = useState(false);
  const [tokenUsage, setTokenUsage] = useState<{
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  } | null>(null);

  // Load providers on mount
  useEffect(() => {
    loadProviders();
  }, []);

  // Auto-select first available model when provider changes
  useEffect(() => {
    const selectedProvider = providers.find((p) => p.name === provider);
    if (selectedProvider && selectedProvider.models && selectedProvider.models.length > 0) {
      setModel(selectedProvider.models[0] || '');
    }
  }, [provider, providers]);

  const loadProviders = async () => {
    try {
      const response = await adminApi.getLLMProviders();
      setProviders(response.providers);

      // Set first configured provider as default
      const firstConfigured = response.providers.find((p: LLMProvider) => p.configured);
      if (firstConfigured) {
        setProvider(firstConfigured.name as 'openrouter' | 'azure' | 'google');
      }
    } catch (err: any) {
      setError('Failed to load LLM providers: ' + (err.message || 'Unknown error'));
    }
  };

  const handleGenerate = async () => {
    if (!userPrompt.trim()) {
      setError('Please enter a workflow description');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedJSON('');
    setIsValidJSON(null);
    setTokenUsage(null);

    try {
      const result = await adminApi.llmInfer({
        provider,
        model,
        systemPrompt,
        userPrompt,
        temperature,
        maxTokens,
      });

      setGeneratedJSON(result.content);
      setTokenUsage(result.usage);

      // Validate generated JSON
      try {
        const parsed = JSON.parse(result.content);
        WorkflowDefinitionSchema.parse(parsed);
        setIsValidJSON(true);
      } catch (validationError: any) {
        setIsValidJSON(false);
        setError('Generated JSON is invalid: ' + validationError.message);
      }
    } catch (err: any) {
      setError('Failed to generate workflow: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApply = () => {
    if (!generatedJSON || !isValidJSON) {
      setError('Cannot apply invalid JSON');
      return;
    }

    try {
      const parsed = JSON.parse(generatedJSON);
      onWorkflowUpdate({
        ...workflow,
        definition: parsed,
      });
      setError('');
      alert('Workflow definition updated successfully!');
    } catch (err: any) {
      setError('Failed to apply workflow: ' + err.message);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedJSON);
    alert('JSON copied to clipboard!');
  };

  const handleResetSystemPrompt = () => {
    setSystemPrompt(DEFAULT_WORKFLOW_SCHEMA);
  };

  const availableModels = providers.find((p) => p.name === provider)?.models || [];
  const isProviderConfigured = providers.find((p) => p.name === provider)?.configured || false;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            AI Workflow Generator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Provider and Model Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="provider">Provider</Label>
              <Select value={provider} onValueChange={(value: any) => setProvider(value)}>
                <SelectTrigger id="provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.name} value={p.name} disabled={!p.configured}>
                      {p.name} {!p.configured && '(Not Configured)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <Select value={model} onValueChange={setModel} disabled={!isProviderConfigured}>
                <SelectTrigger id="model">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {availableModels.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!isProviderConfigured && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Provider not configured. Check backend environment variables.
              </AlertDescription>
            </Alert>
          )}

          {/* System Prompt */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="systemPrompt">System Prompt</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleResetSystemPrompt}
                className="h-8"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset Schema
              </Button>
            </div>
            <Textarea
              id="systemPrompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              className="font-mono text-sm min-h-[200px]"
              placeholder="System prompt with workflow schema..."
            />
          </div>

          {/* User Prompt */}
          <div className="space-y-2">
            <Label htmlFor="userPrompt">Your Request</Label>
            <Textarea
              id="userPrompt"
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              className="min-h-[100px]"
              placeholder="Describe the workflow you want to create..."
            />
          </div>

          {/* Generation Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="temperature">Temperature</Label>
              <Input
                id="temperature"
                type="number"
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value) || 0.7)}
                min={0}
                max={1}
                step={0.1}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxTokens">Max Tokens</Label>
              <Input
                id="maxTokens"
                type="number"
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value) || 4000)}
                min={100}
                max={16000}
              />
            </div>
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || !isProviderConfigured || !model}
            className="w-full"
            size="lg"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Workflow
              </>
            )}
          </Button>

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <Collapsible open={isErrorExpanded} onOpenChange={setIsErrorExpanded}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Show first line or first 100 characters */}
                      <div className="whitespace-pre-wrap">
                        {isErrorExpanded
                          ? error
                          : ((error || '').split('\n')[0] || '').substring(0, 100) + (error && error.length > 100 ? '...' : '')}
                      </div>
                    </div>
                    {(error && error.length > 100) ? (
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-2 h-6 px-2"
                        >
                          {isErrorExpanded ? (
                            <>
                              <ChevronUp className="h-3 w-3 mr-1" />
                              Hide
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-3 w-3 mr-1" />
                              Show Full Error
                            </>
                          )}
                        </Button>
                      </CollapsibleTrigger>
                    ) : null}
                  </div>
                  <CollapsibleContent className="mt-2">
                    <div className="text-sm whitespace-pre-wrap font-mono bg-destructive/10 p-2 rounded border border-destructive/20">
                      {error}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </AlertDescription>
            </Alert>
          )}

          {/* Generated JSON Display */}
          {generatedJSON && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="generatedJSON" className="flex items-center gap-2">
                  Generated JSON
                  {isValidJSON !== null && (
                    <>
                      {isValidJSON ? (
                        <span className="flex items-center text-green-600 text-sm">
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Valid
                        </span>
                      ) : (
                        <span className="flex items-center text-red-600 text-sm">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          Invalid
                        </span>
                      )}
                    </>
                  )}
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    className="h-8"
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleApply}
                    disabled={!isValidJSON}
                    className="h-8"
                  >
                    Apply to Workflow
                  </Button>
                </div>
              </div>
              <Textarea
                id="generatedJSON"
                value={generatedJSON}
                onChange={(e) => setGeneratedJSON(e.target.value)}
                className="font-mono text-sm min-h-[300px]"
              />
            </div>
          )}

          {/* Token Usage */}
          {tokenUsage && (
            <div className="text-sm text-muted-foreground">
              Tokens Used: {tokenUsage.promptTokens.toLocaleString()} prompt +{' '}
              {tokenUsage.completionTokens.toLocaleString()} completion ={' '}
              {tokenUsage.totalTokens.toLocaleString()} total
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
